import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router'
import {
  Form,
  FormInstance,
  ButtonToolbar,
  Button,
  PasswordInput,
  Message,
  Text,
} from 'rsuite'

import Container from '../Container/Container'
import FormField from 'shared/FormField/FormField'

import { useQueryParams } from 'app/lib/utils'
import { hardNavigateWithViewTransition } from 'shared/viewTransition'

type LoginResult = {
  passwordChangeRequired: boolean
  badLogin: boolean
  emailVerificationRequired?: boolean
  serverError?: boolean
  rateLimited?: boolean
}

const RATE_LIMIT_ERROR_MESSAGE = 'Too many attempts. Try again in 15 minutes.'

type LoginQueryParams = {
  c?: string
}

async function doLogin(
  username: string,
  password: string,
): Promise<LoginResult> {
  const response = await fetch('/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password, noRedirect: true }),
  })

  const result = {
    passwordChangeRequired: false,
    badLogin: false,
    emailVerificationRequired: false,
    serverError: false,
    rateLimited: false,
  }

  if (response.ok) {
    const loginResponse: LoginDto = await response.json()
    if (loginResponse.message === 'Password change required') {
      result.passwordChangeRequired = true
    }

    if (loginResponse.message === 'Email verification required') {
      result.emailVerificationRequired = true
    }
  } else {
    if (response.status === 429) {
      result.rateLimited = true
      return result
    }

    if (response.status === 401 || response.status === 403) {
      const loginResponse: string = await response.text()
      console.error('Error logging in', loginResponse)
      result.badLogin = true
    } else {
      const loginResponse: string = await response.text()
      console.error('Server error', loginResponse)
      result.serverError = true
    }
  }

  return result
}

type LoginForm = {
  username: string
  password: string
}

const Login: React.FC = (): React.ReactElement => {
  const navigate = useNavigate()
  const { c: inviteCode } = useQueryParams<LoginQueryParams>()
  const [formValue, setFormValue] = useState<LoginForm>({
    username: '',
    password: '',
  })
  const [loginError, setLoginError] = useState<boolean>(false)
  const [rateLimitError, setRateLimitError] = useState<boolean>(false)
  const [serverError, setServerError] = useState<boolean>(false)

  const formRef = useRef<FormInstance | null>(null)

  const handleLogin = async () => {
    const { username, password } = formValue
    const loginResult = await doLogin(username, password)

    if (loginResult.passwordChangeRequired) {
      navigate('/change-password', { viewTransition: true })
    } else if (loginResult.emailVerificationRequired) {
      // force full reload to come back as authenticated.
      hardNavigateWithViewTransition('/verify-email')
    } else if (loginResult.rateLimited) {
      setRateLimitError(true)
      setLoginError(false)
      setServerError(false)
    } else if (loginResult.badLogin) {
      setLoginError(true)
      setRateLimitError(false)
      setServerError(false)
    } else if (loginResult.serverError) {
      setServerError(true)
      setRateLimitError(false)
      setLoginError(false)
    } else if (inviteCode) {
      // force full page reload to clear any cached user data and load the
      // invite acceptance page properly
      hardNavigateWithViewTransition(`/?c=${inviteCode}`)
    } else {
      hardNavigateWithViewTransition('/')
    }

    return false
  }

  const forgotPasswordClicked = () => {
    navigate('/forgot-password', { viewTransition: true })
  }

  return (
    <Container>
      <Form
        ref={formRef}
        onSubmit={() => {
          handleLogin().catch((error) => {
            console.error('Login submit failed:', error)
          })
        }}
        fluid
        onChange={(value) => setFormValue(value as LoginForm)}
        formValue={formValue}
      >
        <FormField name="username" label="Username" testId="login-username" />
        <FormField
          name="password"
          label="Password"
          accepter={PasswordInput}
          testId="login-password"
        />

        {loginError ? (
          <Message
            type="error"
            closable
            w="100%"
            onClose={() => setLoginError(false)}
          >
            <Text weight="bold">Error!</Text>
            <Text>Invalid username or password. Please try again.</Text>
          </Message>
        ) : null}

        {rateLimitError ? (
          <Message
            type="error"
            closable
            w="100%"
            onClose={() => setRateLimitError(false)}
          >
            <Text weight="bold">Error!</Text>
            <Text>{RATE_LIMIT_ERROR_MESSAGE}</Text>
          </Message>
        ) : null}

        {serverError ? (
          <Message
            type="error"
            closable
            w="100%"
            onClose={() => setServerError(false)}
          >
            <Text weight="bold">Error!</Text>
            <Text>Server error. Please try again.</Text>
          </Message>
        ) : null}

        <Form.Group>
          <ButtonToolbar style={{ justifyContent: 'center' }}>
            <Button type="submit" data-testid="login-submit">
              Login
            </Button>
          </ButtonToolbar>
        </Form.Group>
      </Form>

      <p>
        Forgot username or password?{' '}
        <a onClick={forgotPasswordClicked}>Click Here</a>
      </p>
    </Container>
  )
}

export default Login
